import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export type GuestCancellationReason =
  | "SCHEDULING_CONFLICT"
  | "CHANGED_MIND"
  | "BOOKING_MISTAKE"
  | "FOUND_ANOTHER_SALON"
  | "TOO_EXPENSIVE"
  | "PERSONAL_EMERGENCY"
  | "TRANSPORTATION_ISSUE"
  | "WEATHER"
  | "PREFER_NOT_TO_SAY"
  | "OTHER";

interface EditGuestCancellationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonType: GuestCancellationReason, reasonText: string) => void;
  initialReasonType: GuestCancellationReason;
  initialReasonText: string;
  isLoading?: boolean;
}

export function EditGuestCancellationDialog({
  open,
  onClose,
  onConfirm,
  initialReasonType,
  initialReasonText,
  isLoading = false,
}: EditGuestCancellationDialogProps) {
  const { t } = useTranslation("appointments");
  const [reasonType, setReasonType] = useState<GuestCancellationReason>(initialReasonType);
  const [reasonText, setReasonText] = useState(initialReasonText);

  // Update local state when initial values change
  useEffect(() => {
    if (open) {
      setReasonType(initialReasonType);
      setReasonText(initialReasonText);
    }
  }, [open, initialReasonType, initialReasonText]);

  const handleConfirm = () => {
    onConfirm(reasonType, reasonText);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("editCancellation.title")}</DialogTitle>
          <DialogDescription>{t("editCancellation.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason-type">{t("decline.reasonLabel")}</Label>
            <Select
              value={reasonType}
              onValueChange={(value) => setReasonType(value as GuestCancellationReason)}
              disabled={isLoading}
            >
              <SelectTrigger id="reason-type">
                <SelectValue>
                  {reasonType
                    ? t(`cancellationReasons.${reasonType}`)
                    : t("decline.reasonPlaceholder")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SCHEDULING_CONFLICT">
                  {t("cancellationReasons.SCHEDULING_CONFLICT")}
                </SelectItem>
                <SelectItem value="CHANGED_MIND">
                  {t("cancellationReasons.CHANGED_MIND")}
                </SelectItem>
                <SelectItem value="BOOKING_MISTAKE">
                  {t("cancellationReasons.BOOKING_MISTAKE")}
                </SelectItem>
                <SelectItem value="FOUND_ANOTHER_SALON">
                  {t("cancellationReasons.FOUND_ANOTHER_SALON")}
                </SelectItem>
                <SelectItem value="TOO_EXPENSIVE">
                  {t("cancellationReasons.TOO_EXPENSIVE")}
                </SelectItem>
                <SelectItem value="PERSONAL_EMERGENCY">
                  {t("cancellationReasons.PERSONAL_EMERGENCY")}
                </SelectItem>
                <SelectItem value="TRANSPORTATION_ISSUE">
                  {t("cancellationReasons.TRANSPORTATION_ISSUE")}
                </SelectItem>
                <SelectItem value="WEATHER">{t("cancellationReasons.WEATHER")}</SelectItem>
                <SelectItem value="PREFER_NOT_TO_SAY">
                  {t("cancellationReasons.PREFER_NOT_TO_SAY")}
                </SelectItem>
                <SelectItem value="OTHER">{t("cancellationReasons.OTHER")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason-text">{t("decline.additionalDetailsLabel")}</Label>
            <Textarea
              id="reason-text"
              placeholder={t("details.placeholders.guestCancellationText")}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              disabled={isLoading}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t("editCancellation.saving") : t("editCancellation.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
